import { useLingui } from '@lingui/react/macro';
import { useState, type ReactNode } from 'react';

import { DASH } from '../lib/format';
import { intlLocale } from '../lib/locale';
import manufacturers from '../lib/manufacturerLogos.json';
import './manufacturer-logo.css';

/** Original manufacturer artwork, served locally with the application. */
interface ManufacturerArtwork {
  name: string;
  file: string;
  aliases: string[];
  background?: string;
}

function normalize(name: string): string {
  return name.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss').replace(/[^a-z0-9]/g, '');
}

const LOGOS = new Map<string, ManufacturerArtwork>(
  manufacturers.flatMap((logo: ManufacturerArtwork) =>
    [logo.name, ...logo.aliases].map((name) => [normalize(name), logo] as const)),
);

export default function ManufacturerLogo({ manufacturer = '', className = '' }: { manufacturer?: string; className?: string }): ReactNode {
  const { t } = useLingui();
  const name = manufacturer.trim();
  // Ignore case, spacing and punctuation, but never guess from a partial name.
  const logo = LOGOS.get(normalize(name));
  const src = logo ? `/brand/manufacturers/${logo.file}` : null;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (!src || failedSrc === src) {
    const words = name.match(/[\p{L}\p{N}]+/gu) ?? [];
    const initials = words.length > 1
      ? words.slice(0, 2).map((word) => Array.from(word)[0]).join('')
      : Array.from(words[0] ?? '').slice(0, 2).join('');
    const label = name ? t`Kein Logo für ${name} verfügbar` : t`Hersteller nicht angegeben`;
    return (
      <span className={`manufacturer-logo manufacturer-logo-fallback ${className}`} role="img" aria-label={label} title={label}>
        {initials.toLocaleUpperCase(intlLocale()) || DASH}
      </span>
    );
  }

  return (
    <span className={`manufacturer-logo ${className}`} title={name} style={logo?.background ? { backgroundColor: logo.background } : undefined}>
      <img src={src} alt={t`Logo von ${name}`} width={96} height={30} loading="lazy" decoding="async" onError={() => setFailedSrc(src)} />
    </span>
  );
}
